import { useEffect, useState } from 'react';
import type { Celebration } from './useCelebrations';

// Renders the celebration queue as a stack of transient overlay cards.
// Each card slides in from the top, shows the icon + title + detail,
// bursts a small confetti shower (CSS particles, no library), and
// auto-dismisses after ~4s. `prefers-reduced-motion` disables the
// confetti + slide animation; the card still appears (informational).
//
// Cards stack top-right; newest on top. Dismiss on click or timeout.

const CONFETTI_COUNT = 14;
const DISPLAY_MS = 4200;

export function CelebrationOverlay({ queue, onDismiss }: { queue: Celebration[]; onDismiss: (id: string) => void }) {
  const reduceMotion = usePrefersReducedMotion();
  return (
    <div className="pointer-events-none fixed right-4 top-20 z-50 flex flex-col gap-2 sm:right-6 sm:top-24">
      {queue.map((c) => (
        <CelebrationCard key={c.id} celebration={c} onDismiss={onDismiss} reduceMotion={reduceMotion} />
      ))}
    </div>
  );
}

function CelebrationCard({ celebration, onDismiss, reduceMotion }: { celebration: Celebration; onDismiss: (id: string) => void; reduceMotion: boolean }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    // Trigger the slide-in on mount.
    const t = requestAnimationFrame(() => setVisible(true));
    const timer = setTimeout(() => onDismiss(celebration.id), DISPLAY_MS);
    return () => { cancelAnimationFrame(t); clearTimeout(timer); };
  }, [celebration.id, onDismiss]);

  return (
    <div
      className={`pointer-events-auto relative w-72 overflow-hidden rounded-lg border border-accent/40 bg-bg-panel p-4 shadow-2xl ${reduceMotion ? '' : 'mirabili-slide-in'} ${visible ? 'mirabili-card-visible' : 'mirabili-card-hidden'}`}
      role="status"
      aria-live="polite"
      onClick={() => onDismiss(celebration.id)}
    >
      {/* Confetti burst (suppressed under reduced-motion) */}
      {!reduceMotion && (
        <div className="pointer-events-none absolute inset-0">
          {Array.from({ length: CONFETTI_COUNT }).map((_, i) => (
            <span
              key={i}
              className="mirabili-confetti"
              style={{
                left: `${(i * 100) / CONFETTI_COUNT}%`,
                background: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
                animationDelay: `${(i % 5) * 80}ms`,
              }}
            />
          ))}
        </div>
      )}
      <div className="relative flex items-center gap-3">
        <span className="text-4xl" aria-hidden>{celebration.icon}</span>
        <div>
          <p className="text-sm font-bold text-accent">{celebration.title}</p>
          <p className="text-sm text-ink">{celebration.detail}</p>
        </div>
      </div>
    </div>
  );
}

const CONFETTI_COLORS = ['var(--accent)', 'var(--accent-success)', 'var(--accent-warning)', '#f472b6', '#facc15'];

function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState(false);
  if (typeof window !== 'undefined' && window.matchMedia) {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq.matches !== reduce) setReduce(mq.matches);
  }
  return reduce;
}